import React from 'react';
import { format } from 'date-fns';
import { ChevronDown, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import PaymentHistory from './PaymentHistory';
import {
  Subscription,
  Bill,
  PaymentHistory as PaymentHistoryType
} from '../../types';

interface PaymentHistoryDetailsProps {
  item: Subscription | Bill;
  type: 'subscription' | 'bill';
  paymentHistory: PaymentHistoryType[];
  onBack: () => void;
}

const PaymentHistoryDetails: React.FC<PaymentHistoryDetailsProps> = ({
  item,
  type,
  paymentHistory,
  onBack
}) => {
  // ✅ Proper type-safe date handling
  const nextPaymentDate: Date =
    type === 'subscription'
      ? new Date((item as Subscription).nextCharge)
      : new Date((item as Bill).dueDate);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Button
            variant="ghost"
            onClick={onBack}
            className="mr-2"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <h3 className="text-lg font-semibold">{item.name}</h3>
        </div>

        <Badge
          variant={
            item.status === 'active'
              ? 'success'
              : item.status === 'pending'
              ? 'warning'
              : item.status === 'paused'
              ? 'warning'
              : 'danger'
          }
        >
          {item.status}
        </Badge>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Next Payment
              </span>
              <span className="text-lg font-semibold">
                ${item.amount}
              </span>
              <div className="flex items-center text-xs text-gray-500 mt-1">
                <Clock className="h-3 w-3 mr-1" />
                {format(nextPaymentDate, 'MMM d, yyyy')}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Payment Information
              </span>

              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Frequency:</span>
                  <span className="font-medium">
                    {'frequency' in item ? item.frequency : 'One-time'}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Start Date:</span>
                  <span className="font-medium">
                    {format(new Date(item.startDate), 'MMM d, yyyy')}
                  </span>
                </div>

                {item.endDate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">End Date:</span>
                    <span className="font-medium">
                      {format(new Date(item.endDate), 'MMM d, yyyy')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment History */}
      <PaymentHistory
        payments={paymentHistory}
        showHeader={true}
        compact={false}
      />
    </div>
  );
};

export default PaymentHistoryDetails;